import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhThreatHuntingApi } from 'src/api/mindtime/wazuhThreatHuntingApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhThreatHuntingFindingItem,
  WazuhThreatHuntingQuery,
  WazuhThreatHuntingSummaryData,
} from 'src/types/mindtime/wazuhThreatHunting';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type ThreatHuntingFacets = {
  users: string[];
  mitreIds: string[];
  eventTypes: string[];
};

export type ThreatHuntingFilters = {
  user?: string;
  mitreTechnique?: string;
  eventType?: string;
  severityBand?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type ThreatHuntingState = {
  loading: boolean;
  summary: WazuhThreatHuntingSummaryData | null;
  items: WazuhPagedData<WazuhThreatHuntingFindingItem> | null;
  facets: ThreatHuntingFacets;
};

type BuildThreatHuntingQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: ThreatHuntingFilters;
  page?: number;
  pageSize?: number;
};

const emptyFacets = (): ThreatHuntingFacets => ({
  users: [],
  mitreIds: [],
  eventTypes: [],
});

const emptyState = (): ThreatHuntingState => ({
  loading: true,
  summary: null,
  items: null,
  facets: emptyFacets(),
});

const mergeFacets = (
  items: WazuhThreatHuntingFindingItem[],
  previous: ThreatHuntingFacets
): ThreatHuntingFacets => {
  const users = new Set(previous.users);
  const mitreIds = new Set(previous.mitreIds);
  const eventTypes = new Set(previous.eventTypes);

  items.forEach((item) => {
    if (item.user) users.add(item.user);
    if (item.mitreId) mitreIds.add(item.mitreId);
    if (item.eventType) eventTypes.add(item.eventType);
  });

  return {
    users: Array.from(users).sort((a, b) => a.localeCompare(b)),
    mitreIds: Array.from(mitreIds).sort((a, b) => a.localeCompare(b)),
    eventTypes: Array.from(eventTypes).sort((a, b) => a.localeCompare(b)),
  };
};

const buildThreatHuntingQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  page,
  pageSize,
}: BuildThreatHuntingQueryInput): WazuhThreatHuntingQuery => {
  const { user, mitreTechnique, eventType, severityBand, wazuhAgentId, organizationId } = filters;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(user ? { user } : {}),
    ...(mitreTechnique ? { mitreId: mitreTechnique } : {}),
    ...(eventType ? { eventType } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useThreatHuntingExport = (search = '', filters: ThreatHuntingFilters = {}) => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    setExporting(true);
    mindtimeWazuhThreatHuntingApi
      .export(buildThreatHuntingQuery({ scope, profileOrgId, isReseller, search, filters }))
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'threat_hunting_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useThreatHuntingExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters]);

  return { exporting, handleExport };
};

export const useThreatHuntingData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: ThreatHuntingFilters = {}
): ThreatHuntingState => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<ThreatHuntingState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
        organizationId: filters.organizationId || '',
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller, filters.organizationId]
  );

  const { user, mitreTechnique, eventType, severityBand, wazuhAgentId, organizationId } = filters;

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    let cancelled = false;

    const effectiveScope = buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: organizationId || undefined,
    });

    mindtimeWazuhThreatHuntingApi
      .getSummary(effectiveScope)
      .then((summary) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useThreatHuntingData.summary');
          setState((prev) => ({
            ...prev,
            summary: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhThreatHuntingApi
      .getFindings(
        buildThreatHuntingQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeFacets(items.items, prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useThreatHuntingData.findings');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    user,
    mitreTechnique,
    eventType,
    severityBand,
    wazuhAgentId,
    organizationId,
  ]);

  return state;
};
